import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, X, Edit3, Search, ImageOff, Filter, CheckSquare, Square } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import ImageUploader from "../components/ImageUploader";
import { MediaPicker } from "../components/MediaLibrary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import {
  adminListProducts,
  adminCreateProduct,
  adminUpdateProduct,
  adminDeleteProduct,
  resolveMediaUrl,
} from "../lib/api";

const EMPTY = {
  slug: "",
  name: "",
  tagline: "",
  description: "",
  price_100ml: 0,
  price_50ml: 0,
  brand: "",
  category: "Unisexe",
  collection: "",
  product_type: "perfume",
  badge: "",
  stock: 100,
  image_main: "",
  image_gallery: "",
  top_notes: "",
  heart_notes: "",
  base_notes: "",
  intensity: "",
  longevity: "",
  ingredients: "",
  usage_tip: "",
  specifications: "",
  limited_edition_until: "",
};

const slugify = (s) =>
  s.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const parseList = (v) => (v || "").split(",").map((x) => x.trim()).filter(Boolean);
const parseSpecs = (v) => {
  const obj = {};
  (v || "").split("\n").forEach((line) => {
    const [k, ...rest] = line.split(":");
    if (k && rest.length) obj[k.trim()] = rest.join(":").trim();
  });
  return Object.keys(obj).length ? obj : null;
};
const stringifySpecs = (o) => (o ? Object.entries(o).map(([k, v]) => `${k}: ${v}`).join("\n") : "");

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);
  const [mainPickerOpen, setMainPickerOpen] = useState(false);
  const [editing, setEditing] = useState(null); // product or "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState({ q: "", type: "all" });
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkRulesOpen, setBulkRulesOpen] = useState(false);
  const [bulkRules, setBulkRules] = useState({ match_brand: "", match_note: "", collection: "Orient" });
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  const toggleBulkSelect = (id) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearBulk = () => { setBulkSelected(new Set()); setBulkMode(false); setBulkRulesOpen(false); setBulkPreview(null); };

  const applyBulkAssign = async (collection) => {
    if (bulkSelected.size === 0) return;
    setBulkSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/products/bulk-assign-collection`,
        { product_ids: Array.from(bulkSelected), collection },
        { withCredentials: true }
      );
      toast.success(`${res.data.modified} parfum${res.data.modified > 1 ? "s" : ""} assigné${res.data.modified > 1 ? "s" : ""} à « ${collection || "aucune collection"} »`);
      clearBulk();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'assignation");
    } finally {
      setBulkSaving(false);
    }
  };

  const previewRules = async () => {
    setBulkSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/products/bulk-assign-collection`,
        { collection: "__PREVIEW__", match_brand: bulkRules.match_brand, match_note: bulkRules.match_note },
        { withCredentials: true }
      );
      setBulkPreview(res.data);
    } catch (e) {
      // Server rejects __PREVIEW__ as invalid — fallback: fetch matched locally
      const rx = (s) => (s || "").toLowerCase();
      const matched = products.filter((p) => {
        const brandMatch = bulkRules.match_brand && rx(p.brand).includes(rx(bulkRules.match_brand));
        const noteMatch = bulkRules.match_note && [...(p.top_notes || []), ...(p.heart_notes || []), ...(p.base_notes || [])].some((n) => rx(n).includes(rx(bulkRules.match_note)));
        return brandMatch || noteMatch;
      });
      setBulkPreview({ matched: matched.length, modified: 0, products: matched.map((p) => ({ id: p.id, name: p.name, collection: bulkRules.collection })) });
    } finally {
      setBulkSaving(false);
    }
  };

  const applyRules = async () => {
    setBulkSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/products/bulk-assign-collection`,
        { collection: bulkRules.collection, match_brand: bulkRules.match_brand, match_note: bulkRules.match_note },
        { withCredentials: true }
      );
      toast.success(`${res.data.modified} parfum${res.data.modified > 1 ? "s" : ""} assigné${res.data.modified > 1 ? "s" : ""} par règle`);
      clearBulk();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setBulkSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListProducts();
      setProducts(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return products
      .filter((p) => filter.type === "all" || (p.product_type || "perfume") === filter.type)
      .filter((p) => {
        if (!filter.q) return true;
        const q = filter.q.toLowerCase();
        return p.name.toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q) || p.slug.includes(q);
      });
  }, [products, filter]);

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      slug: p.slug,
      name: p.name,
      tagline: p.tagline || "",
      description: p.description || "",
      price_100ml: p.price_100ml || 0,
      price_50ml: p.price_50ml || 0,
      brand: p.brand || "",
      category: p.category || "Unisexe",
      collection: p.collection || "",
      product_type: p.product_type || "perfume",
      badge: p.badge || "",
      stock: p.stock ?? 100,
      image_main: p.image_main || "",
      image_gallery: (p.image_gallery || []).join(", "),
      top_notes: (p.top_notes || []).join(", "),
      heart_notes: (p.heart_notes || []).join(", "),
      base_notes: (p.base_notes || []).join(", "),
      intensity: p.intensity || "",
      longevity: p.longevity || "",
      ingredients: p.ingredients || "",
      usage_tip: p.usage_tip || "",
      specifications: stringifySpecs(p.specifications),
      limited_edition_until: p.limited_edition_until
        ? new Date(p.limited_edition_until).toISOString().slice(0, 16)
        : "",
    });
  };

  const close = () => { setEditing(null); setForm(EMPTY); };

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        slug: form.slug || slugify(form.name),
        name: form.name,
        tagline: form.tagline,
        description: form.description,
        price_100ml: parseFloat(form.price_100ml) || 0,
        price_50ml: parseFloat(form.price_50ml) || 0,
        brand: form.brand || null,
        category: form.category,
        collection: form.collection || null,
        product_type: form.product_type,
        badge: form.badge || null,
        stock: parseInt(form.stock, 10) || 0,
        image_main: form.image_main,
        image_gallery: parseList(form.image_gallery.replaceAll("\n", ",")),
        top_notes: parseList(form.top_notes),
        heart_notes: parseList(form.heart_notes),
        base_notes: parseList(form.base_notes),
        intensity: form.intensity,
        longevity: form.longevity,
        ingredients: form.ingredients,
        usage_tip: form.usage_tip,
        specifications: parseSpecs(form.specifications),
        limited_edition_until: form.limited_edition_until
          ? new Date(form.limited_edition_until).toISOString()
          : null,
      };
      if (!payload.image_gallery.length) payload.image_gallery = [payload.image_main];

      if (editing === "new") {
        await adminCreateProduct(payload);
        toast.success("Produit créé");
      } else {
        await adminUpdateProduct(editing.id, payload);
        toast.success("Produit mis à jour");
      }
      close();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer définitivement "${p.name}" ?`)) return;
    try {
      await adminDeleteProduct(p.id);
      toast.success("Produit supprimé");
      await load();
    } catch {
      toast.error("Erreur");
    }
  };

  const quickStock = async (p, newStock) => {
    try {
      await adminUpdateProduct(p.id, { stock: parseInt(newStock, 10) || 0 });
      setProducts(products.map((x) => x.id === p.id ? { ...x, stock: parseInt(newStock, 10) || 0 } : x));
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <AdminLayout title="Produits" subtitle="Gérez l'ensemble de votre catalogue : ajouter, modifier, supprimer, ajuster prix, photos, descriptions, stock et catégories.">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 mb-10">
        <div className="flex items-center gap-3 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="products-search"
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              placeholder="Rechercher (nom, marque, slug)..."
              className="input-line pl-9 border border-white/10 px-4 py-2 w-full"
            />
          </div>
          <select
            data-testid="products-filter"
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            className="label-caps text-[10px] bg-transparent border border-white/15 text-white px-3 py-2.5"
          >
            <option value="all" className="bg-[#0a0a0a]">Tous</option>
            <option value="perfume" className="bg-[#0a0a0a]">Parfums</option>
            <option value="watch" className="bg-[#0a0a0a]">Montres</option>
          </select>
        </div>
        <button
          data-testid="bulk-mode-toggle"
          onClick={() => { setBulkMode(!bulkMode); if (bulkMode) clearBulk(); }}
          className={`btn-outline flex items-center gap-2 ${bulkMode ? "border-[color:var(--gold)] text-[color:var(--gold)]" : ""}`}
        >
          <Filter size={14} /> {bulkMode ? "Fermer la sélection" : "Assigner en masse"}
        </button>
        <button data-testid="new-product-btn" onClick={openNew} className="btn-gold">
          <Plus size={14} /> Nouveau produit
        </button>
      </div>

      {bulkMode && (
        <div className="border border-[color:var(--gold)]/30 bg-[color:var(--gold)]/[0.03] p-5 mb-6" data-testid="bulk-toolbar">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="label-caps text-[color:var(--gold)] text-[10px] mb-1">Mode sélection multiple</p>
              <p className="text-white/70 text-sm">
                <span className="text-[color:var(--gold)] font-serif text-lg">{bulkSelected.size}</span> parfum{bulkSelected.size > 1 ? "s sélectionnés" : " sélectionné"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="bulk-select-all"
                onClick={() => setBulkSelected(new Set(filtered.map((p) => p.id)))}
                className="text-xs label-caps text-white/60 hover:text-white border-b border-white/20"
              >
                Tout sélectionner
              </button>
              <button
                data-testid="bulk-select-no-collection"
                onClick={() => setBulkSelected(new Set(filtered.filter((p) => !p.collection).map((p) => p.id)))}
                className="text-xs label-caps text-[color:var(--gold)] hover:text-white border-b border-[color:var(--gold)]/40"
              >
                Sélectionner sans collection
              </button>
              <button onClick={() => setBulkRulesOpen(true)} data-testid="bulk-rules-open" className="text-xs label-caps text-white/60 hover:text-white border-b border-white/20">
                Règles automatiques…
              </button>
            </div>
          </div>
          {bulkSelected.size > 0 && (
            <div className="mt-4 flex flex-wrap gap-2 items-center border-t border-white/10 pt-4">
              <span className="label-caps text-[10px] text-white/40">Assigner à :</span>
              <button
                data-testid="bulk-assign-signature"
                disabled={bulkSaving}
                onClick={() => applyBulkAssign("Signature")}
                className="btn-outline text-xs py-2 px-4"
              >
                Signature
              </button>
              <button
                data-testid="bulk-assign-orient"
                disabled={bulkSaving}
                onClick={() => applyBulkAssign("Orient")}
                className="btn-gold text-xs py-2 px-4"
              >
                Orient
              </button>
              <button
                data-testid="bulk-assign-none"
                disabled={bulkSaving}
                onClick={() => applyBulkAssign("")}
                className="text-xs label-caps text-red-400/70 hover:text-red-300 border-b border-red-400/30"
              >
                Retirer la collection
              </button>
            </div>
          )}
        </div>
      )}

      {/* Products list */}
      {loading ? (
        <p className="text-white/50 label-caps">Chargement...</p>
      ) : (
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <p className="text-white/40 italic py-16 text-center">Aucun produit trouvé.</p>
          ) : (
            filtered.map((p) => (
              <div
                key={p.id}
                data-testid={`product-row-${p.slug}`}
                onClick={bulkMode ? () => toggleBulkSelect(p.id) : undefined}
                className={`border grid grid-cols-12 gap-4 items-center p-4 transition-colors ${
                  bulkMode
                    ? bulkSelected.has(p.id)
                      ? "border-[color:var(--gold)] bg-[color:var(--gold)]/[0.06] cursor-pointer"
                      : "border-white/10 hover:border-white/25 cursor-pointer"
                    : "border-white/10 hover:border-white/25"
                }`}
              >
                {bulkMode && (
                  <div className="col-span-1 md:col-span-1 -mr-2 flex justify-center">
                    {bulkSelected.has(p.id)
                      ? <CheckSquare size={20} className="text-[color:var(--gold)]" strokeWidth={1.5} />
                      : <Square size={20} className="text-white/30" strokeWidth={1.5} />}
                  </div>
                )}
                <div className={bulkMode ? "col-span-1" : "col-span-2 md:col-span-1"}>
                  {p.image_main ? (
                    <img src={resolveMediaUrl(p.image_main)} alt={p.name} className="w-14 h-16 object-cover" />
                  ) : (
                    <div className="w-14 h-16 bg-white/5 flex items-center justify-center"><ImageOff size={16} className="text-white/30" /></div>
                  )}
                </div>
                <div className="col-span-10 md:col-span-4">
                  <p className="label-caps text-[color:var(--gold)] text-[9px] mb-1">{p.brand || (p.product_type === "watch" ? "Montre" : "Parfum")}</p>
                  <p className="font-serif text-white text-lg leading-tight">{p.name}</p>
                  <p className="text-white/40 text-xs mt-1">{p.slug}</p>
                </div>
                <div className="col-span-4 md:col-span-2">
                  <p className="label-caps text-white/40 text-[9px] mb-1">Prix</p>
                  <p className="font-serif text-[color:var(--gold)]">{(p.price_100ml || 0).toFixed(2)}€</p>
                </div>
                <div className="col-span-4 md:col-span-2">
                  <p className="label-caps text-white/40 text-[9px] mb-1">Stock</p>
                  <input
                    type="number"
                    data-testid={`stock-${p.slug}`}
                    defaultValue={p.stock}
                    onBlur={(e) => quickStock(p, e.target.value)}
                    className="bg-transparent border-b border-white/15 text-white text-sm py-1 w-20 focus:border-[color:var(--gold)] outline-none"
                  />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <p className="label-caps text-white/40 text-[9px] mb-1">Catégorie · Collection</p>
                  <p className="text-white/70 text-sm">{p.category}{p.badge ? ` · ${p.badge}` : ""}</p>
                  {p.collection ? (
                    <p className="text-[color:var(--gold)] text-xs mt-0.5" data-testid={`collection-${p.slug}`}>{p.collection}</p>
                  ) : (
                    <p className="text-red-400 text-xs mt-0.5" data-testid={`collection-${p.slug}`}>⚠ Aucune collection</p>
                  )}
                </div>
                <div className="col-span-12 md:col-span-1 flex md:flex-col items-center md:items-end gap-3 justify-end">
                  <button data-testid={`edit-${p.slug}`} onClick={() => openEdit(p)} className="text-[color:var(--gold)] hover:opacity-80" aria-label="Modifier">
                    <Edit3 size={16} />
                  </button>
                  <button data-testid={`delete-${p.slug}`} onClick={() => remove(p)} className="text-red-400 hover:text-red-300" aria-label="Supprimer">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Bulk Rules modal (règles automatiques) */}
      {bulkRulesOpen && (
        <div className="fixed inset-0 z-[85] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div data-testid="bulk-rules-modal" className="bg-[#0A0A0A] border border-white/10 w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <p className="label-caps text-[color:var(--gold)] text-[10px]">Règles automatiques</p>
                <h2 className="font-serif text-xl text-white">Assigner par critère</h2>
              </div>
              <button onClick={() => { setBulkRulesOpen(false); setBulkPreview(null); }} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="label-caps text-[10px] text-white/50 block mb-2">Marque contient (ex. Lattafa, Mauboussin, Paris)</label>
                <input
                  data-testid="bulk-rule-brand"
                  value={bulkRules.match_brand}
                  onChange={(e) => setBulkRules({ ...bulkRules, match_brand: e.target.value })}
                  placeholder="Lattafa"
                  className="input-line w-full"
                />
              </div>
              <div>
                <label className="label-caps text-[10px] text-white/50 block mb-2">OU note contient (ex. oud, ambre, jasmin)</label>
                <input
                  data-testid="bulk-rule-note"
                  value={bulkRules.match_note}
                  onChange={(e) => setBulkRules({ ...bulkRules, match_note: e.target.value })}
                  placeholder="Oud"
                  className="input-line w-full"
                />
              </div>
              <div>
                <label className="label-caps text-[10px] text-[color:var(--gold)]/80 block mb-2">Assigner à la collection</label>
                <select
                  value={bulkRules.collection}
                  onChange={(e) => setBulkRules({ ...bulkRules, collection: e.target.value })}
                  className="input-line bg-transparent w-full"
                  data-testid="bulk-rule-collection"
                >
                  <option value="Signature" className="bg-[#0a0a0a]">Signature</option>
                  <option value="Orient" className="bg-[#0a0a0a]">Orient</option>
                </select>
              </div>

              {bulkPreview && (
                <div className="border border-[color:var(--gold)]/30 bg-[color:var(--gold)]/[0.03] p-4">
                  <p className="label-caps text-[10px] text-[color:var(--gold)] mb-2">Aperçu de l'action</p>
                  <p className="text-white text-sm mb-2"><span className="font-serif text-xl text-[color:var(--gold)]">{bulkPreview.matched}</span> parfum{bulkPreview.matched > 1 ? "s" : ""} correspondront et seront classés en « {bulkRules.collection} ».</p>
                  <ul className="max-h-32 overflow-y-auto text-xs text-white/60 space-y-0.5" data-testid="bulk-preview-list">
                    {(bulkPreview.products || []).slice(0, 12).map((p) => (
                      <li key={p.id}>• {p.name}</li>
                    ))}
                    {(bulkPreview.products || []).length > 12 && <li className="italic">…et {bulkPreview.products.length - 12} autres</li>}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  data-testid="bulk-rules-preview"
                  onClick={previewRules}
                  disabled={bulkSaving || (!bulkRules.match_brand && !bulkRules.match_note)}
                  className="btn-outline text-xs"
                >
                  Prévisualiser
                </button>
                <button
                  data-testid="bulk-rules-apply"
                  onClick={applyRules}
                  disabled={bulkSaving || (!bulkRules.match_brand && !bulkRules.match_note)}
                  className="btn-gold text-xs"
                >
                  {bulkSaving ? "..." : "Appliquer maintenant"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit / Create modal */}
      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 md:p-8 overflow-y-auto">
          <div data-testid="product-modal" className="bg-[#0A0A0A] border border-white/10 w-full max-w-4xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#0A0A0A] z-10">
              <div>
                <p className="label-caps text-[color:var(--gold)] text-[10px]">{editing === "new" ? "Créer" : "Modifier"}</p>
                <h2 className="font-serif text-2xl text-white">{editing === "new" ? "Nouveau produit" : editing.name}</h2>
              </div>
              <button onClick={close} className="text-white/60 hover:text-white" data-testid="modal-close">
                <X size={22} />
              </button>
            </div>

            <form onSubmit={save} className="p-6 md:p-8 space-y-10">
              {/* Basics */}
              <div>
                <p className="label-caps text-[color:var(--gold)] text-[10px] mb-6">Informations générales</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Nom *</label>
                    <input required data-testid="form-name" value={form.name} onChange={change("name")} className="input-line" />
                  </div>
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Slug (URL) — laissez vide pour auto</label>
                    <input value={form.slug} onChange={change("slug")} placeholder={slugify(form.name)} className="input-line" />
                  </div>
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Marque</label>
                    <input data-testid="form-brand" value={form.brand} onChange={change("brand")} className="input-line" placeholder="Lattafa, Loui Martin, Boss…" />
                  </div>
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Type</label>
                    <select value={form.product_type} onChange={change("product_type")} className="input-line bg-transparent">
                      <option className="bg-[#0a0a0a]" value="perfume">Parfum</option>
                      <option className="bg-[#0a0a0a]" value="watch">Montre</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Catégorie</label>
                    <select value={form.category} onChange={change("category")} className="input-line bg-transparent">
                      <option className="bg-[#0a0a0a]">Unisexe</option>
                      <option className="bg-[#0a0a0a]">Femme</option>
                      <option className="bg-[#0a0a0a]">Homme</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-caps text-[color:var(--gold)]/80 text-[9px] block mb-2">
                      Collection · où l'afficher sur le site
                    </label>
                    <select
                      value={form.collection}
                      onChange={change("collection")}
                      data-testid="admin-product-collection"
                      className="input-line bg-transparent"
                    >
                      <option className="bg-[#0a0a0a]" value="">— Choisir une collection —</option>
                      <option className="bg-[#0a0a0a]" value="Signature">Signature · Parfums européens</option>
                      <option className="bg-[#0a0a0a]" value="Orient">Orient · Parfums Dubaï & Moyen-Orient</option>
                    </select>
                    {!form.collection && (
                      <p className="text-[color:var(--gold)]/70 text-[10px] mt-1 italic">
                        ⚠ Sans collection, le parfum n'apparaîtra ni en accueil ni dans /collection/*.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Badge</label>
                    <select value={form.badge} onChange={change("badge")} className="input-line bg-transparent">
                      <option className="bg-[#0a0a0a]" value="">Aucun</option>
                      <option className="bg-[#0a0a0a]">Nouveau</option>
                      <option className="bg-[#0a0a0a]">Meilleure vente</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Accroche (tagline)</label>
                    <input value={form.tagline} onChange={change("tagline")} className="input-line" placeholder="Une phrase élégante qui résume le parfum" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Description</label>
                    <textarea rows={4} value={form.description} onChange={change("description")} className="input-line resize-none" />
                  </div>
                </div>
              </div>

              {/* Pricing / Stock */}
              <div>
                <p className="label-caps text-[color:var(--gold)] text-[10px] mb-6">Prix & Stock</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Prix 100ml (€) *</label>
                    <input required data-testid="form-price" type="number" step="0.01" value={form.price_100ml} onChange={change("price_100ml")} className="input-line" />
                  </div>
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Prix 50ml (€) — 0 si non proposé</label>
                    <input type="number" step="0.01" value={form.price_50ml} onChange={change("price_50ml")} className="input-line" />
                  </div>
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Stock (unités)</label>
                    <input type="number" data-testid="form-stock" value={form.stock} onChange={change("stock")} className="input-line" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="label-caps text-white/40 text-[9px] block mb-2">
                      Édition limitée — fin (optionnel)
                    </label>
                    <input
                      type="datetime-local"
                      data-testid="form-limited-until"
                      value={form.limited_edition_until}
                      onChange={change("limited_edition_until")}
                      className="input-line"
                    />
                    <p className="text-white/30 text-[10px] mt-1.5">
                      Renseignez une date/heure future pour afficher un compte à rebours d'urgence sur la fiche produit. Laisser vide pour désactiver.
                    </p>
                  </div>
                </div>
              </div>

              {/* Photos */}
              <div>
                <p className="label-caps text-[color:var(--gold)] text-[10px] mb-6">Photos</p>
                <div className="space-y-5">
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Photo principale *</label>
                    <div className="flex gap-2 mb-3">
                      <ImageUploader
                        onUploaded={(url) => setForm((f) => ({ ...f, image_main: url }))}
                        label="Uploader"
                        testid="upload-main"
                      />
                      <button
                        type="button"
                        data-testid="pick-main-media"
                        onClick={() => setMainPickerOpen(true)}
                        className="inline-flex items-center gap-2 border border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[color:var(--gold)] hover:text-black px-4 py-2 label-caps text-[10px] tracking-[0.25em] transition-colors"
                      >
                        Médiathèque
                      </button>
                    </div>
                    <input
                      required
                      data-testid="form-image"
                      value={form.image_main}
                      onChange={change("image_main")}
                      className="input-line"
                      placeholder="ou coller une URL d'image (https://...)"
                    />
                    {form.image_main && (
                      <div className="mt-3 border border-white/10 inline-block p-2">
                        <img src={resolveMediaUrl(form.image_main)} alt="Aperçu" className="h-32 w-24 object-cover" />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Galerie (URLs multiples)</label>
                    <div className="flex gap-2 mb-3">
                      <ImageUploader
                        onUploaded={(url) => setForm((f) => ({ ...f, image_gallery: f.image_gallery ? `${f.image_gallery}, ${url}` : url }))}
                        label="Uploader"
                        testid="upload-gallery"
                      />
                      <button
                        type="button"
                        data-testid="pick-gallery-media"
                        onClick={() => setGalleryPickerOpen(true)}
                        className="inline-flex items-center gap-2 border border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[color:var(--gold)] hover:text-black px-4 py-2 label-caps text-[10px] tracking-[0.25em] transition-colors"
                      >
                        Médiathèque
                      </button>
                    </div>
                    <textarea rows={3} value={form.image_gallery} onChange={change("image_gallery")} className="input-line resize-none" placeholder="URLs séparées par des virgules ou des retours à la ligne." />
                    {form.image_gallery && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {form.image_gallery.split(/[,\n]/).map((u, i) => u.trim() && (
                          <div key={`${u.trim()}-${i}`} className="w-16 h-16 border border-white/10 overflow-hidden">
                            <img src={u.trim().startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u.trim()}` : u.trim()} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Perfume-specific */}
              {form.product_type === "perfume" && (
                <div>
                  <p className="label-caps text-[color:var(--gold)] text-[10px] mb-6">Pyramide olfactive & Détails</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <div className="md:col-span-2">
                      <label className="label-caps text-white/40 text-[9px] block mb-2">Notes de tête (séparées par virgule)</label>
                      <input value={form.top_notes} onChange={change("top_notes")} className="input-line" placeholder="Bergamote, Cardamome, Poivre" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="label-caps text-white/40 text-[9px] block mb-2">Notes de cœur</label>
                      <input value={form.heart_notes} onChange={change("heart_notes")} className="input-line" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="label-caps text-white/40 text-[9px] block mb-2">Notes de fond</label>
                      <input value={form.base_notes} onChange={change("base_notes")} className="input-line" />
                    </div>
                    <div>
                      <label className="label-caps text-white/40 text-[9px] block mb-2">Intensité</label>
                      <select value={form.intensity} onChange={change("intensity")} className="input-line bg-transparent">
                        <option className="bg-[#0a0a0a]" value="">—</option>
                        <option className="bg-[#0a0a0a]">Léger</option>
                        <option className="bg-[#0a0a0a]">Moyen</option>
                        <option className="bg-[#0a0a0a]">Intense</option>
                      </select>
                    </div>
                    <div>
                      <label className="label-caps text-white/40 text-[9px] block mb-2">Tenue</label>
                      <input value={form.longevity} onChange={change("longevity")} className="input-line" placeholder="6-8h" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="label-caps text-white/40 text-[9px] block mb-2">Conseil d'utilisation</label>
                      <input value={form.usage_tip} onChange={change("usage_tip")} className="input-line" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="label-caps text-white/40 text-[9px] block mb-2">Ingrédients</label>
                      <textarea rows={2} value={form.ingredients} onChange={change("ingredients")} className="input-line resize-none" />
                    </div>
                  </div>
                </div>
              )}

              {/* Watch-specific */}
              {form.product_type === "watch" && (
                <div>
                  <p className="label-caps text-[color:var(--gold)] text-[10px] mb-6">Caractéristiques horlogères</p>
                  <label className="label-caps text-white/40 text-[9px] block mb-2">Une ligne par caractéristique — format « Clé: Valeur »</label>
                  <textarea rows={6} value={form.specifications} onChange={change("specifications")} className="input-line resize-none" placeholder={"Matériau: Acier inoxydable\nMouvement: Quartz\nBoîtier: 42 mm"} />
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-6 pt-4 border-t border-white/10 sticky bottom-0 bg-[#0A0A0A] py-6 -mx-6 md:-mx-8 px-6 md:px-8">
                <button type="button" onClick={close} className="label-caps text-white/50 hover:text-white text-[10px]">Annuler</button>
                <button data-testid="form-submit" type="submit" disabled={saving} className="btn-gold">
                  {saving ? "Enregistrement..." : (editing === "new" ? "Créer le produit" : "Enregistrer")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <MediaPicker
        open={mainPickerOpen}
        onClose={() => setMainPickerOpen(false)}
        onSelect={(url) => setForm((f) => ({ ...f, image_main: url }))}
        kind="image"
      />
      <MediaPicker
        open={galleryPickerOpen}
        onClose={() => setGalleryPickerOpen(false)}
        onSelect={(url) => setForm((f) => ({ ...f, image_gallery: f.image_gallery ? `${f.image_gallery}, ${url}` : url }))}
        kind="image"
      />
    </AdminLayout>
  );
};

export default AdminProducts;
